import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';
import { mockCreators, creatorRoles, creatorCountries } from '../../mocks/creators';
import { createCreatorUrl } from '../../hooks/useNavigation';
import AICreatorsSection from './components/AICreatorsSection';

const CreatorsPage = () => {
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [sortBy, setSortBy] = useState('followers');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCreators = useMemo(() => {
    let filtered = [...mockCreators];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(creator =>
        creator.name.toLowerCase().includes(query) ||
        creator.role.toLowerCase().includes(query) ||
        creator.recentWork.toLowerCase().includes(query) ||
        creator.specialties.some(s => s.toLowerCase().includes(query))
      );
    }

    if (selectedRole !== 'all') {
      filtered = filtered.filter(creator => creator.role.toLowerCase() === selectedRole.toLowerCase());
    }

    if (selectedCountry !== 'all') {
      filtered = filtered.filter(creator => creator.country === selectedCountry);
    }

    switch (sortBy) {
      case 'followers':
        filtered.sort((a, b) => b.followers - a.followers);
        break;
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'projects':
        filtered.sort((a, b) => b.projects - a.projects);
        break;
      case 'awards':
        filtered.sort((a, b) => b.awards - a.awards);
        break;
    }

    return filtered;
  }, [searchQuery, selectedRole, selectedCountry, sortBy]);

  const formatFollowers = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
    return count.toString();
  };

  const clearFilters = () => {
    setSelectedRole('all');
    setSelectedCountry('all');
    setSearchQuery('');
    setSortBy('followers');
  };

  // Stats
  const totalFollowers = mockCreators.reduce((sum, c) => sum + c.followers, 0);
  const totalProjects = mockCreators.reduce((sum, c) => sum + c.projects, 0);
  const totalAwards = mockCreators.reduce((sum, c) => sum + c.awards, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-rose-900 via-rose-800 to-amber-900 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Discover Talented Creators
            </h1>
            <p className="text-xl md:text-2xl text-rose-100 mb-8 max-w-3xl mx-auto">
              Meet the visionary artists, directors, actors, and storytellers
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-search-line text-gray-400 text-xl"></i>
                </div>
                <input
                  type="text"
                  placeholder="Search creators, roles, or projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-4 text-gray-900 bg-white rounded-xl border-0 shadow-lg focus:ring-2 focus:ring-rose-500 focus:outline-none text-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* AI Creators Section */}
        <AICreatorsSection />

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 pr-8"
              >
                <option value="all">All Roles</option>
                {creatorRoles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 pr-8"
              >
                <option value="all">All Countries</option>
                {creatorCountries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 pr-8"
              >
                <option value="followers">Most Followers</option>
                <option value="name">Name A-Z</option>
                <option value="projects">Most Projects</option>
                <option value="awards">Most Awards</option>
              </select>
            </div>

            <div className="lg:col-span-2 flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Creators Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredCreators.map((creator) => (
            <Link
              key={creator.id}
              to={createCreatorUrl(creator.slug)}
              className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              {/* Cover Image */}
              <div className="relative h-32 overflow-hidden">
                <img
                  src={creator.coverImage}
                  alt={`${creator.name} cover`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>

              {/* Avatar */}
              <div className="relative px-4 -mt-12">
                <div className="relative inline-block">
                  <img
                    src={creator.avatar}
                    alt={creator.name}
                    className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                  {creator.verified && (
                    <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-gradient-to-br from-rose-500 to-amber-500 rounded-full flex items-center justify-center border-2 border-white">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-4 pt-2">
                <h3 className="font-bold text-lg text-gray-900 group-hover:text-rose-600 transition-colors">
                  {creator.name}
                </h3>
                <p className="text-sm text-rose-600 font-medium mb-2">{creator.role}</p>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{creator.bio}</p>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                  <span className="flex items-center">
                    <i className="ri-user-follow-line mr-1"></i>
                    {formatFollowers(creator.followers)}
                  </span>
                  <span className="flex items-center">
                    <i className="ri-film-line mr-1"></i>
                    {creator.projects} projects
                  </span>
                  <span className="flex items-center">
                    <i className="ri-award-line mr-1"></i>
                    {creator.awards}
                  </span>
                </div>

                {/* Specialties */}
                <div className="flex flex-wrap gap-1">
                  {creator.specialties.slice(0, 3).map((specialty, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-rose-50 text-rose-600 text-xs rounded-full"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>

                {/* Recent Work */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    <span className="font-medium">Recent:</span> {creator.recentWork}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Empty State */}
        {filteredCreators.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-user-search-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No creators found</h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your filters or search terms to find more creators.
            </p>
            <button
              onClick={clearFilters}
              className="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CreatorsPage;
